import { FEATURE_KEYS } from '@/types';
import type { RangeKey } from '@/types/admin';
import type { FeatureAdoptionRow, PlatformAnalyticsReport, PlatformDashboardData } from '@/types/platform';
import { featureLabel } from '@/lib/featureRegistry';
import { buildReport } from '../admin/insights';
import type { PlatformAnalyticsService, PlatformDashboardService, PlatformSessionService } from '../../platformContracts';
import { PLATFORM_ROLE_PERMISSIONS } from '@/lib/permissions';
import { ServiceError } from '../../contracts';
import { db } from '../db';
import { adminRoot, delay, pctx, platformRoot, tenantRow } from './context';

const DAY = 86_400_000;
const days = (r: RangeKey) => (r === '7d' ? 7 : r === '30d' ? 30 : 90);
const dayKey = (t: number) => new Date(t).toISOString().slice(0, 10);

export const platformSession: PlatformSessionService = {
  async me() {
    const user = db.user;
    if (!user) throw new ServiceError('Please sign in to continue', 'UNAUTHORIZED');
    if (user.role !== 'SUPER_ADMIN') throw new ServiceError('This console is for platform staff only.', 'FORBIDDEN');
    return delay({ user, permissions: PLATFORM_ROLE_PERMISSIONS[user.role] }, 250);
  },
};

export const platformDashboard: PlatformDashboardService = {
  async summary(range) {
    pctx('platform.dashboard.view');
    const tenants = Object.values(adminRoot());
    const { plans, subscriptions, auditLog } = platformRoot();
    const anchor = Date.now();
    const from = anchor - days(range) * DAY;

    const activeSubs = subscriptions.filter((s) => s.status === 'ACTIVE');
    const mrr = activeSubs.reduce((s, x) => s + x.amount, 0);
    const mrrPrev = activeSubs.filter((s) => Date.parse(s.createdAt) < from).reduce((s, x) => s + x.amount, 0);
    const activeTenants = tenants.filter((t) => t.status !== 'SUSPENDED').length;
    const activeTenantsPrev = tenants.filter((t) => t.status !== 'SUSPENDED' && Date.parse(t.createdAt) < from).length;
    const totalCustomers = tenants.reduce((s, t) => s + t.customers.length, 0);
    const totalCustomersPrev = tenants.reduce((s, t) => s + t.customers.filter((c) => Date.parse(c.joinedAt) < from).length, 0);
    const reports = tenants.map((t) => buildReport(t, range));
    const gmv = reports.reduce((s, r) => s + r.kpis.revenue.value, 0);
    const gmvPrev = reports.reduce((s, r) => s + r.kpis.revenue.previous, 0);

    const buckets = new Map<string, number>();
    for (let i = days(range) - 1; i >= 0; i--) buckets.set(dayKey(anchor - i * DAY), 0);
    activeSubs.forEach((s) => { const k = dayKey(Date.parse(s.createdAt)); if (buckets.has(k)) buckets.set(k, (buckets.get(k) ?? 0) + s.amount); });
    let running = mrr - [...buckets.values()].reduce((a, b) => a + b, 0);
    const mrrSeries = [...buckets.entries()].map(([date, add]) => { running += add; return { date, mrr: running }; });

    const growthBuckets = new Map<string, number>();
    for (let i = days(range) - 1; i >= 0; i--) growthBuckets.set(dayKey(anchor - i * DAY), 0);
    let base = tenants.filter((t) => Date.parse(t.createdAt) < from).length;
    const tenantGrowth = [...growthBuckets.keys()].map((date) => {
      base += tenants.filter((t) => dayKey(Date.parse(t.createdAt)) === date).length;
      return { date, tenants: base };
    });

    const planDistribution = plans.map((p) => ({ planId: p.id, planName: p.name, count: tenants.filter((t) => t.planId === p.id).length })).filter((p) => p.count > 0);
    const recentTenants = [...tenants].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 5).map(tenantRow);
    const recentAudit = auditLog.slice(0, 6);
    const trialsEndingSoon = tenants.filter((t) => t.status === 'TRIAL' && anchor - Date.parse(t.createdAt) > 7 * DAY).length;
    const pastDueCount = subscriptions.filter((s) => s.status === 'PAST_DUE').length;
    const suspendedCount = tenants.filter((t) => t.status === 'SUSPENDED').length;

    const data: PlatformDashboardData = {
      kpis: {
        mrr: { value: mrr, previous: mrrPrev }, activeTenants: { value: activeTenants, previous: activeTenantsPrev },
        totalCustomers: { value: totalCustomers, previous: totalCustomersPrev }, gmv: { value: gmv, previous: gmvPrev },
      },
      mrrSeries, tenantGrowth, planDistribution, recentTenants, recentAudit, trialsEndingSoon, pastDueCount, suspendedCount,
    };
    return delay(data, 500);
  },
};

export const platformAnalytics: PlatformAnalyticsService = {
  async report(range) {
    pctx('platform.analytics.view');
    const tenants = Object.values(adminRoot());
    const { plans, subscriptions } = platformRoot();
    const activeSubs = subscriptions.filter((s) => s.status === 'ACTIVE');
    const mrr = activeSubs.reduce((s, x) => s + x.amount, 0);
    const cancelled = subscriptions.filter((s) => s.status === 'CANCELLED').length;
    const churnRate = subscriptions.length ? (cancelled / subscriptions.length) * 100 : 0;
    const reports = tenants.map((t) => ({ t, r: buildReport(t, range) }));
    const gmv = reports.reduce((s, { r }) => s + r.kpis.revenue.value, 0);

    const anchor = Date.now();
    const buckets = new Map<string, number>();
    for (let i = days(range) - 1; i >= 0; i--) buckets.set(dayKey(anchor - i * DAY), 0);
    activeSubs.forEach((s) => { const k = dayKey(Date.parse(s.createdAt)); if (buckets.has(k)) buckets.set(k, (buckets.get(k) ?? 0) + s.amount); });
    let running = mrr - [...buckets.values()].reduce((a, b) => a + b, 0);
    const mrrSeries = [...buckets.entries()].map(([date, add]) => { running += add; return { date, mrr: running }; });

    const planDistribution = plans.map((p) => ({ planId: p.id, planName: p.name, count: tenants.filter((t) => t.planId === p.id).length, mrr: activeSubs.filter((s) => s.planId === p.id).reduce((s, x) => s + x.amount, 0) })).filter((p) => p.count > 0);
    const featureAdoption: FeatureAdoptionRow[] = FEATURE_KEYS.map((key) => ({
      key, label: featureLabel(key), totalTenants: tenants.length,
      enabledTenants: tenants.filter((t) => t.features[key]).length, overriddenTenants: tenants.filter((t) => key in t.featureOverrides).length,
    }));
    const topTenants = reports.sort((a, b) => b.r.kpis.revenue.value - a.r.kpis.revenue.value).slice(0, 6).map(({ t, r }) => ({ tenantId: t.tenantId, name: t.name, revenue: r.kpis.revenue.value }));

    const data: PlatformAnalyticsReport = { mrr, arr: mrr * 12, gmv, churnRate, mrrSeries, planDistribution, featureAdoption, topTenants };
    return delay(data, 500);
  },
};
